// The vintage basemap style: Protomaps layer definitions, recolored like an old engraved
// survey map, with every point of interest removed (no businesses on the map, ever).
import { layers, namedFlavor } from '@protomaps/basemaps';

const PAPER = '#efe4cc';
const PAPER_DARK = '#e6d7b8';
const INK = '#2b2418';
const HALO = '#f7efdd';
const WATER = '#a9c0c2';

const ROAD_CASING = '#a88f69';
const ROAD_MINOR = '#fbf6ea';
const ROAD_MAJOR = '#f6e6c2';
const ROAD_HIGHWAY = '#ecd29d';

/** Colors only; layer geometry and zoom logic come from @protomaps/basemaps. */
export const vintageFlavor = {
  ...namedFlavor('light'),
  background: PAPER_DARK,
  earth: PAPER,
  park_a: '#dfdfba', park_b: '#cbd09d',
  wood_a: '#d8d9ae', wood_b: '#c3c78f',
  scrub_a: '#dedcb4', scrub_b: '#cfcf9f',
  hospital: '#eadbc4', industrial: '#e2d4b8', school: '#eadcc0', zoo: '#dcdcb6', military: '#e0d3b8',
  pedestrian: '#f1e7d0', glacier: '#f4eee0', sand: '#ecdcb3', beach: '#f0dfb2',
  aerodrome: '#e4d8bf', runway: '#efe6d3',
  water: WATER,
  pier: '#d9c7a3',
  buildings: '#d8c6a1',
  railway: '#6e5a44', boundaries: '#a08c6e',
  other: '#efe4cc', minor_service: ROAD_MINOR, minor_a: ROAD_MINOR, minor_b: ROAD_MINOR,
  link: ROAD_MAJOR, major: ROAD_MAJOR, highway: ROAD_HIGHWAY,
  minor_service_casing: ROAD_CASING, minor_casing: ROAD_CASING, link_casing: ROAD_CASING,
  major_casing_early: '#9a7f56', major_casing_late: '#9a7f56',
  highway_casing_early: '#86683f', highway_casing_late: '#86683f',
  tunnel_other_casing: '#cdbd9c', tunnel_minor_casing: '#cdbd9c', tunnel_link_casing: '#cdbd9c',
  tunnel_major_casing: '#cdbd9c', tunnel_highway_casing: '#cdbd9c',
  tunnel_other: '#efe6d3', tunnel_minor: '#efe6d3', tunnel_link: '#efe6d3', tunnel_major: '#efe6d3', tunnel_highway: '#efe6d3',
  bridges_other_casing: '#86683f', bridges_minor_casing: '#86683f', bridges_link_casing: '#86683f',
  bridges_major_casing: '#86683f', bridges_highway_casing: '#86683f',
  bridges_other: ROAD_MINOR, bridges_minor: ROAD_MINOR, bridges_link: ROAD_MAJOR, bridges_major: ROAD_MAJOR, bridges_highway: ROAD_HIGHWAY,
  roads_label_minor: '#3f3122', roads_label_minor_halo: HALO,
  roads_label_major: INK, roads_label_major_halo: HALO,
  ocean_label: '#3e5c66',
  subplace_label: '#5e4e38', subplace_label_halo: HALO,
  city_label: INK, city_label_halo: HALO,
  state_label: '#7a6a52', state_label_halo: HALO,
  country_label: '#7a6a52',
  address_label: '#7a6a52', address_label_halo: HALO,
  landcover: {
    grassland: '#e3dfbc', barren: '#efe1c0', urban_area: '#e9dcc0', farmland: '#e6dfbb',
    glacier: '#f4eee0', scrub: '#dedcb4', forest: '#d2d4a6',
  },
  regular: 'Noto Sans Regular',
  bold: 'Noto Sans Medium',
  italic: 'Noto Sans Italic',
};
delete vintageFlavor.pois; // no POI layer is generated at all

// Layers that need the sprite (one-way arrows, highway shields) are dropped; we load no sprite.
const DROP = new Set(['roads_oneway', 'roads_shields']);

/** Larger street labels for reading at arm's length while walking. */
const STREET_LABEL_SIZE = ['interpolate', ['linear'], ['zoom'], 14, 11, 16, 13.5, 18, 15.5, 20, 17];

export function vintageLayers(source = 'protomaps') {
  return layers(source, vintageFlavor, { lang: 'en' })
    .filter((l) => l['source-layer'] !== 'pois' && !DROP.has(l.id))
    .map((l) => {
      // Devanagari glyphs aren't self-hosted (no Devanagari names here); fall back to the Latin font.
      const layer = JSON.parse(JSON.stringify(l).replaceAll('Noto Sans Devanagari Regular v1', 'Noto Sans Regular'));
      if (layer.layout) {
        for (const k of Object.keys(layer.layout)) if (k.startsWith('icon-')) delete layer.layout[k];
      }
      if (layer.id === 'roads_labels_minor' || layer.id === 'roads_labels_major') {
        layer.layout['text-size'] = STREET_LABEL_SIZE;
        layer.paint = { ...layer.paint, 'text-halo-width': 2 };
      }
      if (layer.id === 'buildings') layer.paint = { ...layer.paint, 'fill-opacity': 0.85, 'fill-outline-color': '#b8a37c' };
      return layer;
    });
}

/** Complete MapLibre style. URLs are absolute so they work from any page path. */
export function buildStyle({ tilesUrl, glyphsUrl }) {
  return {
    version: 8,
    glyphs: glyphsUrl,
    sources: {
      protomaps: {
        type: 'vector',
        url: `pmtiles://${tilesUrl}`,
        attribution: '<a href="https://protomaps.com">Protomaps</a> © <a href="https://openstreetmap.org/copyright">OpenStreetMap</a>',
      },
    },
    layers: vintageLayers('protomaps'),
  };
}
